//
import { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Toast } from '@belong/ui';
import {
  TourRequestModel,
  UnitListingDetailsModel,
  UserModel,
  ListingApplicationListingApplicationModel,
} from 'api/models';
import AppsCard from 'containercomponents/TourAndAppsCard/AppsCard';
import TourAndAppsCard from 'containercomponents/TourAndAppsCard/TourAndAppsCard';
import { TourRequestCard } from 'containercomponents/TourAndAppsCard/TourRequestCard';
import { useToastWithQueryParams } from 'hooks/useToastWithQueryParams';
import { useUserIdentityVerification } from 'hooks/useUserIdentityVerification';
import RenterAccountBookmark from 'models/renterAccounts/RenterAccountBookmark';
import RenterAccountLease from 'models/renterAccounts/RenterAccountLease';
import AccountPageSection from 'pages/Accounts/Pages/AccountPage/AccountPageSection/AccountPageSection';
import {
  fetchListingApplicationDetail,
  fetchUserProfiles,
} from 'store/redux/renter-accounts/actions';
import {
  selectListingApplicationBookmarks,
  selectOfferedAndSignedLeases,
  selectTourBookmarks,
  selectActiveTourRequests,
  selectListingApplicationDetails,
} from 'store/redux/renter-accounts/selectors';
import { AppState } from 'store/redux/types';
import { selectUser } from 'store/redux/user/selectors';
import AccountPage from '../AccountPage/AccountPage';

type Props = {
  user: UserModel;
  tours: RenterAccountBookmark[];
  listingApplications: RenterAccountBookmark[];
  leases: RenterAccountLease[];
  tourRequests: (TourRequestModel & { homeDetails: UnitListingDetailsModel })[];
  listingApplicationDetails: Record<
    string,
    ListingApplicationListingApplicationModel
  >;
  dispatch: (action: any) => void;
};

const ToursAndApps = ({
  user,
  tours = [],
  listingApplications = [],
  tourRequests = [],
  leases,
  listingApplicationDetails,
  dispatch,
}: Props) => {
  const { verification } = useUserIdentityVerification(user?.id, false);

  const { isToastOpen, toastConfig, closeToast } = useToastWithQueryParams({});

  useEffect(() => {
    if (listingApplications && listingApplications.length > 0) {
      listingApplications.forEach((listingApplication) => {
        if (listingApplication.listingApplication?.uniqueId) {
          dispatch(
            fetchListingApplicationDetail(
              listingApplication.listingApplication.uniqueId,
            ),
          );
        }
      });
    }
  }, [listingApplications, dispatch]);

  useEffect(() => {
    if (Object.keys(listingApplicationDetails).length > 0) {
      // Collect all unique user IDs from all applicants
      const allUserIds = new Set();

      Object.values(listingApplicationDetails).forEach((listingApp) => {
        if (listingApp.application?.applicants) {
          listingApp.application.applicants.forEach((applicant) => {
            allUserIds.add(applicant.residentUniqueId);
          });
        }
      });

      const userIdsArray = Array.from(allUserIds);
      if (userIdsArray.length > 0) {
        dispatch(fetchUserProfiles(userIdsArray));
      }
    }
  }, [listingApplicationDetails, dispatch]);

  const standAloneTours = tours.filter((tourItem) => {
    const tourUnitId = tourItem.unit.basicInfo.id;
    return !listingApplications.find(
      (listingApplication) =>
        tourUnitId === listingApplication.unit.basicInfo.id,
    );
  });

  const listingApplicationsWithTourAndLeaseInfo = useMemo(
    () =>
      listingApplications.map((listingApplication) => {
        const lease = leases.find((leaseItem) => {
          const leaseListingApplicationId =
            leaseItem.leaseInfo.basicInfo.listingApplicationUniqueId;
          return (
            leaseListingApplicationId ===
            listingApplication.listingApplication?.uniqueId
          );
        });

        const withLeaseInfo = lease
          ? { ...listingApplication, leaseInfo: lease.leaseInfo }
          : listingApplication;

        const tour = tours.find(
          (tourItem) =>
            tourItem.unit.basicInfo.id === listingApplication.unit.basicInfo.id,
        );
        if (tour) {
          return {
            ...withLeaseInfo,
            attendeeId: tour.attendeeId,
            attendeeStatus: tour.attendeeStatus,
            displayStatus: tour.displayStatus,
            hasPaymentHold: tour.hasPaymentHold,
            tourId: tour.tourId,
            tourType: tour.tourType,
            visitEndOn: tour.visitEndOn,
            visitStartOn: tour.visitStartOn,
          };
        }
        return withLeaseInfo;
      }),
    [listingApplications, leases, tours],
  );

  const content = (
    <div className="flex flex-col gap-lg pb-3xl">
      {standAloneTours.map((bookmark) => (
        <TourAndAppsCard
          key={`tours-and-applications-${bookmark.applicationId}`}
          bookmark={bookmark}
          user={user}
          isIdentityVerified={
            !!verification && verification.status === 'Passed'
          }
        />
      ))}
      {listingApplicationsWithTourAndLeaseInfo.map((bookmark) => (
        <AppsCard
          key={`tours-and-applications-${bookmark.applicationId}`}
          bookmark={bookmark}
          user={user}
          isIdentityVerified={
            !!verification && verification.status === 'Passed'
          }
          listingApplicationDetails={
            listingApplicationDetails[bookmark.listingApplication?.uniqueId]
          }
          dispatch={dispatch}
        />
      ))}

      {tourRequests.map((tourRequest) => (
        <TourRequestCard
          key={`tour-request-${tourRequest.uniqueId}`}
          tourRequest={tourRequest}
        />
      ))}
    </div>
  );

  return (
    <AccountPage
      headerProps={{
        headerText: 'Tours and Apps',
      }}
    >
      <AccountPageSection content={content} />
      <Toast
        isVisible={isToastOpen}
        onClose={closeToast}
        variant={toastConfig.variant}
      >
        {toastConfig?.message}
      </Toast>
    </AccountPage>
  );
};

function mapStateProps(state: AppState) {
  return {
    user: selectUser(state),
    tours: selectTourBookmarks(state),
    listingApplications: selectListingApplicationBookmarks(state),
    leases: selectOfferedAndSignedLeases(state),
    tourRequests: selectActiveTourRequests(state),
    listingApplicationDetails: selectListingApplicationDetails(state),
  };
}

export default connect(mapStateProps)(ToursAndApps as React.ComponentType<any>);
